/* eslint-disable react/no-unescaped-entities */
/**
 * TermsOfUsePage · /terms
 * ---------------------------------------------------------------------------
 * The rules of engagement. Same TL;DR pattern as the privacy page.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Scale, ShieldAlert, RotateCcw, Ban, Users } from 'lucide-react';
import { useSiteConfig } from '../contexts/SiteConfigContext';

const Section = ({ title, tldr, children, icon: Icon }) => (
  <section className="mb-10" data-testid={`terms-${title.toLowerCase().replace(/\s+/g,'-')}`}>
    <div className="flex items-center gap-3 mb-3">
      {Icon && <div className="w-9 h-9 rounded-lg grid place-items-center" style={{ background: 'rgba(0,255,157,.14)', color: 'var(--eh-green)', border: '1px solid rgba(0,255,157,.4)' }}><Icon size={16} /></div>}
      <h2 className="eh-display font-black" style={{ fontSize: 'clamp(1.3rem, 3vw, 1.8rem)' }}>{title}</h2>
    </div>
    {tldr && (
      <div className="mb-3 p-3 rounded-lg border" style={{ borderColor: 'rgba(0,255,157,.3)', background: 'rgba(0,255,157,.06)' }}>
        <div className="eh-mono text-[10px] tracking-widest text-[var(--eh-green)] mb-1">// TL;DR</div>
        <div className="text-[13px] opacity-90 leading-relaxed" style={{ fontFamily: 'Inter,sans-serif' }}>{tldr}</div>
      </div>
    )}
    <div className="space-y-3 text-[13.5px] opacity-85 leading-relaxed" style={{ fontFamily: 'Inter,sans-serif' }}>
      {children}
    </div>
  </section>
);

const TermsOfUsePage = () => {
  const { config } = useSiteConfig();
  const site = config.site || {};
  const name = site.name || 'ErrorHacker';
  const email = site.email || 'legal@errorhacker.site';
  return (
    <div className="min-h-screen">
      <section className="relative py-16 sm:py-20 eh-grid-bg overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(60% 50% at 50% 40%, rgba(0,255,157,0.08), transparent 60%)' }} />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="eh-kicker justify-center mb-4">// LEGAL · TERMS</div>
          <h1 className="eh-display font-black leading-tight" style={{ fontSize: 'clamp(2rem, 6vw, 4rem)' }}>TERMS OF <span className="eh-neon">USE</span></h1>
          <div className="mt-3 eh-mono text-[11px] opacity-70">Effective: February 2026</div>
        </div>
      </section>

      <section className="py-14 sm:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <Section title="Acceptance" icon={Scale} tldr={`By using ${name}, you agree to these terms. If you disagree with any of them, don't use the service.`}>
            <p>These Terms of Use ("Terms") govern your access to and use of https://errorhacker.site, the {name} Telegram bot, and any services offered by {name} (collectively, the "Services"). By creating an account, placing an order, or otherwise using the Services, you accept these Terms in full.</p>
            <p>You must be at least 18 years old (or the age of majority in your jurisdiction) to use the Services. If you're using the Services on behalf of an organisation, you represent that you have authority to bind that organisation.</p>
          </Section>

          <Section title="Authorised use only" icon={ShieldAlert} tldr="We only work on assets you own or have written permission to test. Any misuse gets you banned + reported to law enforcement.">
            <p>All services provided by {name} are strictly for <strong className="text-white">authorised, ethical, and educational</strong> engagements. You warrant that:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>You own the account, domain, IP, or asset you're asking us to work on — OR you have documented, written permission from the owner</li>
              <li>You will not use our Services for unauthorised access, credential stuffing, doxing, harassment, revenge campaigns, SIM swaps, fraud, or any illegal activity</li>
              <li>You understand that our consultation offering is <strong className="text-white">coaching only</strong> — we help you use the target platform&apos;s <em>official</em> recovery / appeal flow. We do not log in to your account or bypass any platform&apos;s authentication</li>
              <li>You will indemnify {name} against any claims arising from your misuse of the Services</li>
            </ul>
            <p className="text-red-300"><strong>Violation of this section results in immediate termination without refund and, where required by law, referral to law enforcement.</strong></p>
          </Section>

          <Section title="Account & wallet">
            <p>Your account and wallet balance are non-transferable. You are responsible for all activity that occurs under your account. Do not share credentials.</p>
            <p>Wallet top-ups are non-refundable once used to fulfil an order. Unused balance may be refunded on request, minus the payment processor fee (typically 2%).</p>
            <p>We reserve the right to freeze wallets exhibiting suspicious activity (rapid top-ups, spin farming, chargeback history, multi-accounting) pending review.</p>
          </Section>

          <Section title="Refunds & guarantees" icon={RotateCcw} tldr="Consultation: money-back if we can't help in the first session. Books: instant delivery, non-refundable. Custom audits: milestone-based.">
            <p><strong className="text-white">Cyber-Incident Consultation</strong> — priced up-front. If we can&apos;t provide actionable value in your first session (typically 30–60 minutes), you get a full refund. We never guarantee an outcome from a third-party platform (Instagram, Google, Meta, etc.), but we do guarantee the quality of our coaching.</p>
            <p><strong className="text-white">Digital books</strong> — instant download, non-refundable once accessed. If the download link fails, we replace it free of charge.</p>
            <p><strong className="text-white">Memberships</strong> — cancel anytime, prorated refund for the unused period.</p>
            <p><strong className="text-white">Custom engagements</strong> — milestone-based. 50% up front, 50% on delivery. Full refund if we fail to meet a documented milestone.</p>
          </Section>

          <Section title="Payments">
            <p>We accept Cashfree (India), manual UPI, crypto (USDT/USDC/BTC), and wallet balance. Prices are shown inclusive of applicable taxes.</p>
            <p>Chargebacks initiated without contacting us first result in permanent account termination.</p>
          </Section>

          <Section title="Content ownership">
            <p>All content on the Services — including the {name} logo, code, illustrations, warrior avatars, book manuscripts, blog posts, and video walkthroughs — is owned by {name} or its licensors and protected by copyright.</p>
            <p>Books purchased grant you a personal, non-transferable licence to read the content. You may not redistribute, publish excerpts, or use book material in commercial training datasets without written permission.</p>
          </Section>

          <Section title="Prohibited conduct" icon={Ban} tldr="Don't be a jerk. Don't break the law. Don't try to reverse-engineer our systems.">
            <ul className="list-disc pl-5 space-y-1">
              <li>No scraping of the site beyond ordinary use (our robots.txt is authoritative)</li>
              <li>No reverse-engineering, decompiling, or attempting to access non-public APIs</li>
              <li>No creating multiple accounts to bypass wallet, referral, or spin-wheel limits</li>
              <li>No sharing of premium book content, membership access, or promo codes</li>
              <li>No impersonation of {name} operators, moderators, or other users</li>
              <li>No harassment of other users or team members through any channel</li>
            </ul>
          </Section>

          <Section title="Termination" icon={Users}>
            <p>You may close your account at any time from your dashboard. {name} may suspend or terminate your account for material breach of these Terms, prolonged inactivity (24 months), or when required by law.</p>
            <p>On termination, your data is deleted per our Privacy Policy (see <Link to="/privacy" className="eh-neon underline decoration-dotted">/privacy</Link>). Wallet balance is refunded to the original payment method or paid out via UPI, minus processor fees.</p>
          </Section>

          <Section title="Disclaimers">
            <p>The Services are provided "as is" without warranty of any kind. While we work hard to keep the Services running and secure, we do not guarantee uninterrupted availability, error-free operation, or specific outcomes for engagements beyond what is expressly stated in your order.</p>
            <p>To the maximum extent permitted by law, {name}'s aggregate liability for any claim arising from your use of the Services is limited to the amount you paid us in the 6 months preceding the claim.</p>
          </Section>

          <Section title="Changes">
            <p>We may update these Terms from time to time. Material changes will be announced by email and via a notice on the Services at least 14 days before taking effect. Continued use after the effective date constitutes acceptance.</p>
          </Section>

          <Section title="Governing law">
            <p>These Terms are governed by the laws of India. Any dispute arising from these Terms will be resolved by the courts of Bengaluru, India, unless another jurisdiction is required by applicable consumer-protection law.</p>
          </Section>

          <Section title="Contact">
            <p>Legal enquiries: <a href={`mailto:${email}`} className="eh-neon underline decoration-dotted">{email}</a></p>
            <p>General questions: see the <Link to="/faq" className="eh-neon underline decoration-dotted">FAQ</Link> or DM us on <a href={site.telegram || '#'} className="eh-neon underline decoration-dotted">Telegram</a>.</p>
          </Section>

          <div className="mt-10 text-center flex flex-wrap justify-center gap-2">
            <Link to="/privacy" className="eh-btn-ghost text-xs">PRIVACY POLICY</Link>
            <Link to="/about" className="eh-btn-ghost text-xs">ABOUT US</Link>
            <Link to="/" className="eh-btn-primary text-xs">BACK TO HOME</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsOfUsePage;
